import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';
import { captureException } from '@sentry/remix';
import { MobileModalElement, ModalElement, type Survey } from '@shared/dream-components/src';
import SurveyRenderer from '@shared/dream-components/src/surveyElement/SurveyRenderer';
import { useQueryClient } from '@tanstack/react-query';

import Button from '~/components/Button';
import WebinarSeatsInfo from '~/components/WebinarSeatsInfo';
import useIsMobile from '~/hooks/useIsMobile';
import useWebinarDetails, { webinarDetailsKeys } from '~/hooks/useLoaders/useWebinarDetails';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { FormResponse } from '~/interfaces/form';
import type { CalComFulfillmentMetadata } from '~/interfaces/fulfillment';
import type { RootLoaderData } from '~/root';
import { mapCalComBookingError } from '~/utils/cal_com/mapBookingError';
import { extractFormResponses } from '~/utils/extractFormResponses';
import { generateWebinarAccessToken } from '~/utils/generateWebinarAccessToken';
import { getLocalTimeZone } from '~/utils/getLocalTimeZone';
import toast from '~/utils/toast';

interface BookWebinarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookingSuccess: (bookingData: CalComBooking, webinarAccessToken: string, formResponses: FormResponse[]) => void;
  form?: Survey;
  shouldDisableConfirmButton?: boolean;
  productSlug: string;
  eventName: string;
  eventStartsAt: string;
  eventEndsAt?: string;
  eventTimeZone: string;
  remainingSeats?: number;
  maxSeats?: number;
  currentSubscriberEmail?: string;
  currentSubscriberName?: string;
  fulfillmentMetadata?: CalComFulfillmentMetadata;
  onBookingAlreadyExists?: () => void;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDate(startIso: string, timeZone: string): string {
  const start = new Date(startIso);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone,
  }).format(start);
}

function formatTimeRange(startIso: string, endIso: string | undefined, timeZone: string): string {
  const start = new Date(startIso);
  const startTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  }).format(start);

  if (endIso) {
    const end = new Date(endIso);
    const endTime = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(end);
    return `${startTime} - ${endTime}`;
  }

  return startTime;
}

function formatDuration(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const minutes = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BookWebinarModal = ({
  isOpen,
  onClose,
  onBookingSuccess,
  shouldDisableConfirmButton = false,
  productSlug,
  eventName,
  eventStartsAt,
  eventEndsAt,
  eventTimeZone,
  remainingSeats,
  maxSeats,
  currentSubscriberEmail,
  currentSubscriberName,
  fulfillmentMetadata,
  onBookingAlreadyExists,
  form,
}: BookWebinarModalProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const isRescheduling = Boolean(fulfillmentMetadata?.cal_booking_uid);
  const registrationForm = isRescheduling ? undefined : form;
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const publication = rootData?.site?.publication;

  const { data: freshData } = useWebinarDetails({ slug: productSlug, enabled: isOpen });

  const displayRemainingSeats = freshData?.remaining_seats ?? remainingSeats;
  const displayMaxSeats = freshData?.max_seats ?? maxSeats;
  const displayStartsAt = freshData?.event_starts_at ?? eventStartsAt;
  const displayEndsAt = freshData?.event_ends_at ?? eventEndsAt;
  const displayTimeZone = getLocalTimeZone(freshData?.iana_time_zone ?? freshData?.time_zone ?? eventTimeZone);

  const initialEmail = fulfillmentMetadata?.cal_booking_fields_responses?.email ?? currentSubscriberEmail ?? '';
  const initialName = fulfillmentMetadata?.cal_booking_fields_responses?.name ?? currentSubscriberName ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync form state when modal reopens with different subscriber data
  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setName(initialName);
      setIsSubmitting(false);
    }
  }, [isOpen, initialEmail, initialName]);

  const isEmailLocked = Boolean(currentSubscriberEmail);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email.trim() || !name.trim()) {
        return;
      }

      const submittedForm = e.currentTarget instanceof HTMLFormElement ? e.currentTarget : null;

      setIsSubmitting(true);
      const webinarAccessToken = generateWebinarAccessToken();
      const attendeeTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      try {
        const formData = new FormData();
        formData.set('product_slug', productSlug);
        formData.set('name', name.trim());
        formData.set('email', email.trim());
        formData.set('time_zone', attendeeTimeZone);

        if (fulfillmentMetadata?.cal_booking_uid) {
          formData.set('reschedule_uid', fulfillmentMetadata.cal_booking_uid);
        }

        const response = await fetch('/webinars/booking', {
          method: 'POST',
          body: formData,
        });

        const json = await response.json();

        if (!response.ok) {
          if (response.status === 409) {
            if (onBookingAlreadyExists) {
              onBookingAlreadyExists();
            } else {
              const conflictMessage = json?.message;
              toast.error(conflictMessage || t('products.error.booking_conflict'));
            }
            return;
          }

          const rawError: string | undefined = json?.message;
          const mappedKey = mapCalComBookingError(rawError);
          const friendlyError = mappedKey ? t(mappedKey) : rawError;
          captureException(new Error(`Booking failed (${response.status}): ${rawError || JSON.stringify(json)}`));
          toast.error(
            friendlyError ||
              t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }),
          );
          return;
        }

        if (json?.status === 'success' && json?.data?.uid) {
          const formResponses = submittedForm ? extractFormResponses(new FormData(submittedForm)) : [];
          const bookingData: CalComBooking = {
            uid: json.data.uid,
            seatUid: json.data.seatUid,
            attendees: json.data.attendees ?? [{ email: email.trim(), name: name.trim() }],
            bookingFieldsResponses: json.data.bookingFieldsResponses ?? {
              email: email.trim(),
              guests: [],
              location: { value: '', optionValue: '' },
              name: name.trim(),
              notes: '',
            },
          };
          queryClient.invalidateQueries({ queryKey: webinarDetailsKeys.bySlug(productSlug) });
          onBookingSuccess(bookingData, webinarAccessToken, formResponses);
        } else {
          captureException(new Error(`Unexpected booking response: ${JSON.stringify(json)}`));
          toast.error(t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }));
        }
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)));
        toast.error(t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, name, fulfillmentMetadata, onBookingAlreadyExists, onBookingSuccess, productSlug, queryClient, t],
  );

  if (!isOpen) {
    return null;
  }

  const datePart = formatDate(displayStartsAt, displayTimeZone);
  const timePart = formatTimeRange(displayStartsAt, displayEndsAt, displayTimeZone);
  const duration = formatDuration(displayStartsAt, displayEndsAt);
  const isDisabled = isSubmitting || shouldDisableConfirmButton;

  // -------------------------------------------------------------------------
  // Event details panel (left on desktop, top on mobile)
  // -------------------------------------------------------------------------

  const eventDetailsPanel = (
    <div className="flex flex-col gap-6 p-6 md:p-8 md:w-[280px] md:min-w-[280px] bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200">
      {/* Publication identity */}
      {publication && (
        <div className="flex items-center gap-2.5">
          {publication.logo?.thumb?.url && (
            <img
              src={publication.logo.thumb.url}
              alt={publication.name}
              className="w-8 h-8 rounded-full object-cover ring-1 ring-gray-200"
            />
          )}
          <span className="text-sm text-gray-500 font-medium truncate">{publication.name}</span>
        </div>
      )}

      {/* Event title */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 leading-snug">
          {isRescheduling ? t('products.webinar.reschedule_title', { defaultValue: 'Reschedule webinar' }) : eventName}
        </h2>
        {duration && <p className="text-sm text-gray-400 mt-1">{duration}</p>}
      </div>

      {/* Date & time */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2.5">
          <CalendarDaysIcon className="w-[18px] h-[18px] text-gray-400 mt-0.5 shrink-0" />
          <span className="text-sm text-gray-600">{datePart}</span>
        </div>
        <div className="flex items-start gap-2.5">
          <ClockIcon className="w-[18px] h-[18px] text-gray-400 mt-0.5 shrink-0" />
          <span className="text-sm text-gray-600">{timePart}</span>
        </div>
      </div>

      <WebinarSeatsInfo remainingSeats={displayRemainingSeats} maxSeats={displayMaxSeats} />
    </div>
  );

  // -------------------------------------------------------------------------
  // Booking form (right on desktop, bottom on mobile)
  // -------------------------------------------------------------------------

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

  const bookingForm = (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6 md:p-8 md:w-[320px] bg-white">
      <div className="space-y-1.5">
        <label htmlFor="booking-name" className="block text-sm font-medium text-gray-700">
          {t('products.webinar.name_label', { defaultValue: 'Name' })}
          <span className="ml-1" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="booking-name"
          name="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputClasses}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="booking-email" className="block text-sm font-medium text-gray-700">
          {t('products.webinar.email_label', { defaultValue: 'Email' })}
          <span className="ml-1" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="booking-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isEmailLocked}
          required
          className={inputClasses}
        />
      </div>

      {registrationForm && (
        <div
          style={
            {
              '--gap': '16px',
              '--text-align': 'left',
              '--question-required-color': '#000',
              '--question-label-color': '#374151',
              '--question-label-font-family': 'inherit',
              '--question-label-font-size': '14px',
              '--input-text-font-size': '14px',
              '--input-text-color': '#374151',
              '--input-placeholder-color': '#374151',
              '--question-label-font-weight': '500',
            } as CSSProperties
          }
        >
          <SurveyRenderer
            email=""
            hasLocalStorageEmail={false}
            survey={registrationForm}
            isSubmitting={isSubmitting || shouldDisableConfirmButton}
            showSubmitButton={false}
            showEmailInput={false}
            showQuestionNumber={false}
          />
        </div>
      )}

      <div className="[&_button]:!rounded-md">
        <Button type="submit" variant="primary" block disabled={isDisabled} isLoading={isSubmitting}>
          {isRescheduling
            ? t('products.webinar.reschedule_button', { defaultValue: 'Reschedule' })
            : t('products.webinar.register_button', { defaultValue: 'Register' })}
        </Button>
      </div>
    </form>
  );

  // -------------------------------------------------------------------------
  // Combined content
  // -------------------------------------------------------------------------

  const modalContent = (
    <div className="flex flex-col md:flex-row bg-white overflow-hidden">
      {eventDetailsPanel}
      {bookingForm}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <MobileModalElement
        isOpen={isOpen}
        onOpenChange={onClose}
        isLoading={isDisabled}
        showCloseButton={!isDisabled}
        hideOverflow={false}
        element={{
          type: 'mobileModal',
          content: [],
        }}
      >
        {modalContent}
      </MobileModalElement>,
      document.body,
    );
  }

  return createPortal(
    <ModalElement
      isOpen={isOpen}
      onOpenChange={onClose}
      isLoading={isDisabled}
      showCloseButton
      hideOverflow={false}
      element={{
        type: 'modal',
        content: [],
      }}
    >
      {modalContent}
    </ModalElement>,
    document.body,
  );
};
